import { getStepFullLabel } from '@/modules/solutions/educationalProgramStep';
import {
  css,
  useTheme,
} from '@emotion/react';
import map from 'lodash.map';
import size from 'lodash.size';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';

import { Campus } from '@/modules/core/data/types/Campus';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import CircleBtn from '@/components/dataDisplay/CircleBtn';
import EllipsisText from '@/components/dataDisplay/EllipsisText';
import { Stamp } from '@/components/dataDisplay/Stamp';
import Tooltip from '@/components/dataDisplay/Tooltip';

type Props = {
  contextFiltersSettings: ContextFiltersSettings;
  educationalProgramSteps?: EducationalProgramStep[];
  campuses?: Campus[];
  categories?: StudentSolutionsCategory[];
}

/**
 * Display the stamps of the solution (campuses, categories, educational programs, etc.).
 *
 * Each Stamp uses Ellipsis text to avoid the text taking too much space.
 */
const SolutionStamps: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    contextFiltersSettings, educationalProgramSteps, campuses, categories,
  } = props;
  const { t } = useTranslation();
  const theme: CustomerTheme = useTheme();
  const { secondaryColor, onSecondaryColor } = theme;

  const renderTooltip = (labels: Array<string>): JSX.Element => {
    return (
      <span>
        {
          map(labels, (label: string) => {
            return (
              <Fragment key={label}>
                {label}<br />
              </Fragment>
            );
          })
        }
      </span>
    );
  };

  return (
    <Fragment>
      <div
        css={css`
          margin-right: 30px;
          text-align: right;

          @media (max-width: 991.98px) {
            margin-right: 0;
          }

          .rounded-info {
            background-color: ${secondaryColor};
            border-radius: 15px;
            color: ${onSecondaryColor};
            padding: 5px 7px;
          }
        `}
      >
        {
          !!size(educationalProgramSteps) ? (
            <Tooltip
              overlay={renderTooltip(map(educationalProgramSteps, (step: EducationalProgramStep) => {
                return getStepFullLabel(step);
              }))}
            >
              <Stamp>
                {
                  size(educationalProgramSteps) > 1 ? (
                    <Fragment>
                      <EllipsisText>
                        {t('solutionsPage.solutionCard.programsGroup.label', 'Programmes')}
                      </EllipsisText>
                      &nbsp;
                      <CircleBtn mode={'secondary-variant'}>
                        {size(educationalProgramSteps)}
                      </CircleBtn>
                    </Fragment>
                  ) : (
                    <EllipsisText>
                      {educationalProgramSteps?.[0]?.label}
                    </EllipsisText>
                  )
                }
              </Stamp>
            </Tooltip>
          ) : null
        }

        {
          contextFiltersSettings.campusFilterEnabled && !!size(campuses) ? (
            <Tooltip overlay={renderTooltip(map(campuses, 'label'))}>
              <Stamp>
                {
                  size(campuses) > 1 ? (
                    <Fragment>
                      <EllipsisText>
                        {t('solutionsPage.solutionCard.campusesGroup.label', 'Campus')}
                      </EllipsisText>
                      &nbsp;
                      <CircleBtn mode={'secondary-variant'}>
                        {size(campuses)}
                      </CircleBtn>
                    </Fragment>
                  ) : (
                    <EllipsisText>
                      {campuses?.[0]?.label}
                    </EllipsisText>
                  )
                }
              </Stamp>
            </Tooltip>
          ) : null
        }

        {
          categories && !!size(categories) ? (
            <Tooltip overlay={renderTooltip(map(categories, 'label'))}>
              <Stamp>
                {
                  size(categories) > 1 ? (
                    <Fragment>
                      <EllipsisText>
                        {t('solutionsPage.solutionCard.categoriesGroup.label', 'Catégories')}
                      </EllipsisText>
                      &nbsp;
                      <CircleBtn mode={'secondary-variant'}>
                        {size(categories)}
                      </CircleBtn>
                    </Fragment>
                  ) : (
                    <EllipsisText>
                      {categories?.[0].label}
                    </EllipsisText>
                  )
                }
              </Stamp>
            </Tooltip>
          ) : null
        }
      </div>
    </Fragment>
  );
};

export default SolutionStamps;
